/**
 * IfAdminPermission — component-level admin-portal permission gate.
 *
 * Renders `children` iff `useAdminPermissions().can(permission)`
 * returns true; otherwise renders `fallback` (default `null`).
 *
 * Disjoint from the existing `<RequirePermission>` primitive (which
 * gates on the customer-user `PermissionSlug` union from
 * `@artemis/contracts` per CLAUDE.md §3). See
 * `apps/admin/src/shared/auth/admin-permissions.ts` for the
 * dual-namespace naming rationale.
 *
 * Behavior matches the `<RequirePermission>` precedent on the
 * pre-load window: before `/auth/me` resolves (or while `me.staff`
 * is undefined in production), the gate renders the fallback. The
 * cost of a missing-then-appearing affordance is a layout shift;
 * the cost of a wrongly-rendered "+ Add staff" button is a 403 on
 * submit — the former is the cheaper failure mode.
 *
 * Dev-mode permissive fallback (per
 * `.design-incoming/admin-20260522/COMPONENT_API.md §Host-provided
 * context`): in dev builds, when `me.staff` is undefined every
 * `can()` returns true → the gate renders `children`. In production
 * with no staff record, the gate renders `fallback`.
 */

import * as React from 'react';
import { type AdminPermissionSlug } from './admin-permissions.js';
import { useAdminPermissions } from './use-admin-permissions.js';

export interface IfAdminPermissionProps {
  readonly permission: AdminPermissionSlug;
  readonly fallback?: React.ReactNode;
  readonly children: React.ReactNode;
}

export function IfAdminPermission({
  permission,
  fallback = null,
  children,
}: IfAdminPermissionProps): React.ReactElement | null {
  const { can } = useAdminPermissions();
  if (can(permission)) return <>{children}</>;
  return <>{fallback}</>;
}
